import type { TableColumnsType } from 'antd';
import { Divider, Image, Select, Table } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { PermissionOrder } from '../../../../../permissions/PermissionOrder';
import orderStore from '../../../../../stores/orderStore';
import { formatDateAndTime } from '../../../../../utils/dateFunctions';
import { formatCurrency, valueStatusConvert } from '../../../../../utils/functions';
import { orderStatusRules } from '../../../../../utils/selects';
import PermissionComponent from '../../../../atoms/PermissionComponent';
import ContentBox from '../../../../molecules/ContentBox';
import LoadingComponent from '../../../../molecules/LoadingComponent';
import Permission from '../../../../molecules/Permission';
import ModalComponent from '../../../../organisms/ModalComponent';
import StatusRuleComponent from '../StatusRuleComponent';
import ActionButton from '../../../../atoms/ButtonComponent';




interface DataType {
    baseQty: any;
    amount: any;
    total: any;
    name: any;
    skuCode: any;
    description: any;
    code: any;
    uuid: any;
    key: React.Key;
}

interface DescriptionItemProps {
    title: string;
    content: string;
    className?: string;
}

const DescriptionItem = ({ title, content, className }: DescriptionItemProps) => (
    <div title={content} className={`${className} flex gap-2 pb-2`}>
        <p className="text-sm inline-block text-gray-500 font-normal">{title}:</p>
        <p className="text-sm text-zinc-700 font-medium">{content}</p>
    </div>
);

const OrderDetailsPayment: React.FC = observer(({ orderStatus, setOrderStatus }: any) => {
    const { order, loading } = orderStore;
    const [orderHistory, setOrderHistory] = useState<any>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectOrderStatus, setSelectOrderStatus] = useState<string>();
    const [currentStatusSelect, setCurrentStatusSelect] = useState<any>();
    const [nextStatus, setNextStatus] = useState<any>();

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Imagem',
            dataIndex: 'product',
            key: 'product.files',
            render: (item) => {
                return (
                    <Image
                        width={50}
                        //@ts-ignore
                        src={item?.files.length > 0 && item?.files[0]?.path}
                    />
                );
            },
        },
        {
            title: 'SKU',
            dataIndex: 'product',
            key: 'product.skuCode',
            render: (item) => {
                return (
                    <>{item && <p>{item.skuCode}</p>}</>
                );
            },
        },
        {
            title: 'Nome',
            dataIndex: 'product',
            key: 'product.name',
            render: (item) => {
                return (
                    <>{item && <p>{item.name}</p>}</>
                );
            },
        },
        {
            title: 'Preço Unitario',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            render: (item) => {
                return (
                    <>{item && <p>{formatCurrency(item)}</p>}</>
                );
            },
        },
        {
            title: 'Quantidade',
            dataIndex: 'amount',
            key: 'amount',
            render: (item, record) => {
                return (
                    <>{record && <p>{record.baseQty * record.amount}</p>}</>
                );
            },
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: (item) => {
                return (
                    <>{item && <p>{formatCurrency(item)}</p>}</>
                );
            },
        },
    ];


    useEffect(() => {
        if (orderStatus?.length > 0) {
            const statusHistory = orderStatus as any;

            function getFutureStatusOptions() {
                // Ordena o array por data de criação (descendente)
                const sorted = [...statusHistory].sort(
                    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
                );

                // Pega o último status aplicado
                const lastStatusName = sorted[0].newStatus?.statusName;
                setCurrentStatusSelect(lastStatusName);

                // Encontra o ID numérico correspondente
                const currentStatus = orderStatusRules.find((s) => s.value === lastStatusName);

                if (!currentStatus) return [];

                const next = orderStatusRules
                    .filter((s) => parseInt(s.id) > parseInt(currentStatus.id))
                    .sort((a, b) => parseInt(a.id) - parseInt(b.id))[0]; // pega o primeiro (próximo)

                setNextStatus(next);
            }

            getFutureStatusOptions();
        }
    }, [orderStatus, order])


    const openModalChangeStatus = (status: any) => {
        setIsModalOpen(true);
        setSelectOrderStatus(status);
    }


    const changeOrderDetails = async () => {
        if (order && selectOrderStatus) {
            const response = await orderStore.changeOrderStatus(selectOrderStatus, order?.uuid, order?.customer?.uuid);

            if (!response?.data?.error) {
                setCurrentStatusSelect(selectOrderStatus);
                setIsModalOpen(false);
                setOrderStatus(response?.data?.statusChanges)
            }
        }
    }

    const handleCancel = () => {
        setCurrentStatusSelect(currentStatusSelect); // limpa o temporário
        setIsModalOpen(false);
    };

    console.log('order', order);

    return (
        <Permission permissionKeys={[PermissionOrder.GET]} fallback={<PermissionComponent />}>
            <ContentBox borderCustom='rounded-b-lg' title="Informações do Pedido" description='Informações do Pedido' className="mb-4 p-6" actions={
                <>
                    {order?.bonusStatus !== 'PENDING' && currentStatusSelect && currentStatusSelect !== 'DELIVERED' && currentStatusSelect !== 'CANCELED' && (
                        <div className="flex flex-col items-start">

                            {currentStatusSelect !== 'DELIVERED' && currentStatusSelect !== 'CANCELED' &&
                                <div className="flex gap-2">
                                    <ActionButton actionType="delete" onClick={() => openModalChangeStatus('CANCELED')}>Cancelar</ActionButton>
                                    <ActionButton actionType="export" onClick={() => openModalChangeStatus(nextStatus?.value)}>Alterar Status</ActionButton>
                                </div>
                            }
                        </div>
                    )}
                </>
            }>

                <div className="flex gap-4">
                    <div className="w-full">
                        {!loading && order ?
                            <StatusRuleComponent
                                orderStatus={orderStatusRules}
                                statusChanges={orderStatus}
                                paymentOptionName={order?.paymentDetails?.paymentOptionName}
                                order={order}
                            /> : <LoadingComponent />
                        }

                        <Divider />

                        <p className="pb-4 text-base text-primary-color">Informação do Pedido</p>

                        <div className="grid grid-cols-2 gap-8">
                            <DescriptionItem title="Numero" content={order?.idOrder} />
                            <DescriptionItem title="Data criação" content={formatDateAndTime(order?.created)} />
                        </div>

                        <div className="grid grid-cols-2 gap-8" >
                            <DescriptionItem title="Status" content={valueStatusConvert(order?.status?.statusName)} />
                            <DescriptionItem title="Pedido Bonus" content={order?.bonus === true ? 'Bônus' : 'Vendas'} />
                        </div>

                        <Divider />

                        <p className="pb-3 text-base text-primary-color">Dados de Pagamento</p>

                        <div className="grid grid-cols-2 gap-8" >
                            <DescriptionItem title="Pagamento" content={order?.paymentDetails?.paymentType} />
                            <DescriptionItem title="Total" content={formatCurrency(order?.total)} />
                        </div>

                        <div className="grid grid-cols-2 gap-8" >
                            <DescriptionItem title="Numero de parcelas" content={order?.paymentDetails?.installments} />
                            <DescriptionItem title="Valor da parcela" content={formatCurrency(order?.paymentDetails?.installmentValue)} />
                        </div>

                        <div className="grid grid-cols-2 gap-8" >
                            <DescriptionItem title="Desconto de cupom" content={formatCurrency(order?.voucherDiscounts)} />
                            <DescriptionItem title="Desconto de campanha" content={formatCurrency(order?.campaignDiscounts)} />
                        </div>

                        <div className="grid grid-cols-2 gap-8" >
                            <DescriptionItem title="SubTotal" content={formatCurrency(order?.totalValue)} />
                            <DescriptionItem title="Total Faturado" content={formatCurrency(order?.totalBilled)} />
                        </div>

                        <Divider />

                        <Table
                            columns={columns}
                            className="table-custom"
                            dataSource={order?.orderItems?.map((item: any, index: number) => ({
                                ...item,
                                key: item.uuid || index,
                            }))}
                            pagination={false}
                            loading={loading}
                            size="middle"
                            showSorterTooltip={{ target: 'sorter-icon' }}
                        />
                    </div>
                </div>
            </ContentBox>


            {
                isModalOpen && <ModalComponent
                    title="Aviso"
                    content={<p>Você deseja alterar o Status <b>{orderStatusRules.find((item) => item?.value === selectOrderStatus)?.label}</b>?</p>}
                    isModalOpen={isModalOpen}
                    handleOk={() => changeOrderDetails()}
                    handleCancel={() => handleCancel()}
                />
            }
        </Permission >
    );
});

export default OrderDetailsPayment;